import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

import '../dashboard/Dashboard.css';
import axios from 'axios';
import Loader from "../util/Loader";
import { DataGrid } from '@mui/x-data-grid';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CopyAllIcon from '@mui/icons-material/CopyAll';

const baseApiUri = process.env.REACT_APP_API_URL;

export default function AssistantsAdmin() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAssistantName, setNewAssistantName] = useState('');
  const [newAssistantDescription, setNewAssistantDescription] = useState('');
  const [newAssistantType, setNewAssistantType] = useState('');
  const navigate = useNavigate();

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    { field: 'name', headerName: 'Name', flex: 3, editable: true },
    {
      field: 'edit',
      headerName: 'Edit',
      renderCell: ({ row }) => (
        <strong>
          <EditOutlinedIcon
            className="App-icon-spacing App-icon-cursor"
            onClick={() => navigate(`/assistantadmin/${row.id}`)}
          />
        </strong>
      )
    },
    {
      field: 'sandbox',
      headerName: 'Sandbox',
      renderCell: ({ row }) => (
        <strong>
          <CopyAllIcon
            className="App-icon-spacing App-icon-cursor"
            onClick={() => navigate(`/assistantadmin/${row.id}/sandbox`)}
          />
        </strong>
      )
    }
  ];

  const loadAssistants = async () => {
    setLoading(true);
    try {
      const endpoint = `${baseApiUri}/ai/assistant?key=09848734-8745-afrt-8745-8745873487`;
      const res = await axios.get(endpoint);
      const filtered = res.data.assistants.filter(a => a.sandbox == null);
      setRows(filtered);
    } catch (error) {
      console.error('Error loading assistants:', error);
    } finally {
      setLoading(false);
    }
  };

  /** ✅ Create Assistant with required payload structure */
  const handleCreateAssistant = async () => {
    if (!newAssistantName.trim()) return;

    const description = newAssistantDescription.trim();
    const type = newAssistantType.trim();

    // Build payload according to specification
    const payload = {
      active: true,
      name: newAssistantName.trim(),
      type: type || '',
      description: description || '',
      definition: {
        top_p: 1,
        instructions:
          "You're a Vermont Early Childhood assistant, helping to provide insights and analyis of Vermont's young children based on both the yearly \n" +
          '"How Are Vermont\'s Young Children" or "State of Vermont\'s Children" reports and the online indicators provided by the website www.vermontkidsdata.org. You\n' +
          'also have a set of functions that can provide specific data points from these reports. If information is available either from a report or from a function,\n' +
          'you should favor the function.\n\n' +
          '**Instructions:**\n  \n' +
          'You are tasked with analyzing and summarizing early childhood data. When you are asked a question you will refer to the How Are Vermont\'s Young Children reports.\n' +
          'These reports are text files prefaced by how_are_vermonts_young_children and then the year the report was published, for example how_are_vermonts_young_children_2023.txt.\n' +
          '\nNext you will try to compare this to similar data from previous reports, providing insights on trends in the data. Include both positive trends and concerns.\n' +
          'Assume your target audience is Vermont legislators and policy makers.\n' +
          '\nUse markdown-format links to the sources provided.\n',
        tool_resources: {
          code_interpreter: { file_ids: [] },
          file_search: { vector_store_ids: [] }
        },
        metadata: {},
        response_format: "auto",
        name: newAssistantName.trim(),
        temperature: 1,
        description: description || '',
        model: "gpt-4o",
        tools: [{ type: "code_interpreter" }, { type: "file_search" }]
      }
    };

    try {
      const endpoint = `${baseApiUri}/ai/assistant?key=09848734-8745-afrt-8745-8745873487`;
      await axios.post(endpoint, payload);
      // reset fields
      setNewAssistantName('');
      setNewAssistantDescription('');
      setNewAssistantType('');
      setShowAddModal(false);
      await loadAssistants();
    } catch (error) {
      console.error('Error creating assistant:', error);
    }
  };

  useEffect(() => {
    loadAssistants();
  }, []);

  return (
    <div style={{ textAlign: "left" }}>
      <Loader open={loading} />
      <Container>
        <Box sx={{ padding: '20px' }}>
          <div className="admin-main" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>AI Assistants</h1>

            <Button variant="contained" color="secondary" onClick={() => setShowAddModal(true)}>
              + Add New Assistant
            </Button>
          </div>

          <Box sx={{ height: 500, width: '100%', backgroundColor: '#ffffff' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } }
              }}
              pageSizeOptions={[5]}
              checkboxSelection
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Container>

      {/* ✅ Enhanced Add Assistant Modal */}
      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Assistant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Assistant Name"
            fullWidth
            variant="outlined"
            value={newAssistantName}
            onChange={(e) => setNewAssistantName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            value={newAssistantDescription}
            onChange={(e) => setNewAssistantDescription(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Type"
            fullWidth
            variant="outlined"
            value={newAssistantType}
            onChange={(e) => setNewAssistantType(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleCreateAssistant}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

